import { isTauri } from '@/lib/platform';

/**
 * The web app's end of the desktop shell (`src-tauri/src/lib.rs`).
 *
 * The native menu sends each item's id as a `menu` event; ids of the form
 * `navigate:/path#hash` are routes, the rest are actions the caller supplies.
 * Files from Finder, Explorer, the Dock or File → Import are announced with
 * `files-opened` and collected here, one read per file, so the web app gets
 * the same `File` objects a drop would give it.
 */

export interface DesktopHandlers {
  navigate: (to: string, hash: string | undefined) => void;
  openFiles: (files: File[]) => void;
  action: (id: string) => void;
}

function basename(path: string): string {
  return path.split(/[\\/]/).pop() ?? path;
}

async function collectOpenedFiles(open: (files: File[]) => void): Promise<void> {
  const { invoke } = await import('@tauri-apps/api/core');
  const paths = await invoke<string[]>('take_opened_files');
  const files: File[] = [];
  for (const path of paths) {
    try {
      const bytes = await invoke<ArrayBuffer>('read_opened_file', { path });
      files.push(new File([bytes], basename(path)));
    } catch {
      // Moved or deleted between the double-click and the read. The rest
      // still open.
    }
  }
  if (files.length > 0) {
    open(files);
  }
}

/** Parses `navigate:/logbook/export#import` into a route and a hash. */
export function parseMenuId(id: string): { to: string; hash: string | undefined } | null {
  if (!id.startsWith('navigate:')) {
    return null;
  }
  const target = id.slice('navigate:'.length);
  const hashAt = target.indexOf('#');
  return hashAt === -1
    ? { to: target, hash: undefined }
    : { to: target.slice(0, hashAt), hash: target.slice(hashAt + 1) };
}

/** @returns a function that stops listening. Does nothing outside the desktop app. */
export async function startDesktopBridge(handlers: DesktopHandlers): Promise<() => void> {
  if (!isTauri()) {
    return () => undefined;
  }
  const { listen } = await import('@tauri-apps/api/event');
  const stopMenu = await listen<string>('menu', ({ payload }) => {
    const route = parseMenuId(payload);
    if (route === null) {
      handlers.action(payload);
    } else {
      handlers.navigate(route.to, route.hash);
    }
  });
  const stopFiles = await listen('files-opened', () => {
    void collectOpenedFiles(handlers.openFiles);
  });
  // Anything opened before the page was listening — a double-clicked file
  // that launched the app.
  void collectOpenedFiles(handlers.openFiles);
  return () => {
    stopMenu();
    stopFiles();
  };
}
